import { skipToken } from "@reduxjs/toolkit/query";
import { asAccountId } from "@repo/app-types/entities/account";
import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { Navigate, useLocation, useParams } from "react-router";
import { usePlaybookTeammateGradingQuery } from "../../api/entities/performancePlaybook.js";
import { appPaths } from "../appPaths.js";
import { requireRouteParam, type RouteParams } from "../routeParams.js";

/**
 * Generic redirect component for backwards compatibility with old routes.
 * Navigates to a URL built from the current route's params (an
 * `appPaths.x.build(...)` call fed by `requireRouteParam`, so the target
 * gaining or renaming a param is a compile error), preserving query
 * parameters.
 */
export function ParamsRedirect({
  buildTo,
}: {
  buildTo: (params: RouteParams) => string;
}) {
  const params = useParams();
  const location = useLocation();

  return <Navigate to={`${buildTo(params)}${location.search}`} replace />;
}

/**
 * Backwards-compatibility redirect for old `(teammate, account)`-keyed grading URLs.
 *
 * It reads the current URL's `:playbookId` / `:teammateId` / `:accountId` params plus a
 * trailing splat (`*`), resolves the unsplit grading for that tuple to its `gradingId`,
 * then redirects to the new `customer-interactions/:gradingId` URL (preserving the trailing
 * sub-view + query/hash).
 *
 * `buildTo` builds the destination grading page URL from the current URL's params plus the
 * resolved `gradingId`. Note `accountId` is used ONLY to resolve the grading — it never
 * appears in the destination; `teammateId` / `criterionId` / etc. remain only because they
 * are real segments of the destination page hierarchy.
 *
 * Example (by-teammate family):
 *   URL in:   /performance/playbooks/PB/teammates/TM/accounts/ACC/assessment/criteria/CRIT
 *   buildTo:  appPaths.teammateCall.build({ playbookId, teammateId, gradingId })
 *   resolve:  grading for (PB, TM, ACC) -> GID
 *   navigate: /performance/playbooks/PB/teammates/TM/customer-interactions/GID/assessment/criteria/CRIT
 */
export function GradingTupleRedirect({
  buildTo,
}: {
  buildTo: (params: RouteParams, gradingId: string) => string;
}) {
  const params = useParams();
  const location = useLocation();

  const { playbookId, teammateId, accountId } = params;
  const rest = params["*"] ?? "";

  const { data: grading, isError } = usePlaybookTeammateGradingQuery(
    playbookId && teammateId && accountId
      ? {
          playbookId: asPlaybookId(playbookId),
          teammateId: asTeammateId(teammateId),
          accountId: asAccountId(accountId),
        }
      : skipToken,
  );

  if (isError) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  if (!grading) {
    // Still resolving the grading id; render nothing until it arrives.
    return null;
  }

  const suffix = rest ? `/${rest}` : "";
  return (
    <Navigate
      to={`${buildTo(params, grading.id)}${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}

/**
 * Backwards-compatibility redirect for the old `calls` tab segment under
 * `/agents/:teammateAgentId`. The tab was renamed to `customer-interactions`, so this
 * forwards `/agents/:teammateAgentId/calls` (and any sub-path under it) to the new base,
 * preserving the splat, search, and hash. Old tuple redirect URLs
 * (`calls/teammates/.../accounts/.../*`) keep their splat and resolve via the tuple
 * redirect that lives under the new `customer-interactions` base.
 */
export function AgentCallsCompatRedirect() {
  const params = useParams();
  const location = useLocation();

  const teammateAgentId = requireRouteParam(params, "teammateAgentId");
  const rest = params["*"] ?? "";
  const suffix = rest ? `/${rest}` : "";

  return (
    <Navigate
      to={`${appPaths.teammateAgentCalls.build({ teammateAgentId })}${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}
